import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { API_END_POINTS } from "@app/constant/api.constant";
import { AuthService } from "@service/auth.service";
import { BehaviorSubject, Observable } from "rxjs";

@Injectable({
  providedIn: "root",
})
export class TradingService {
  private layoutSetting = new BehaviorSubject<any>({
    showHeader: true,
    showFooter: true,
  });
  private hasCallCreated = new BehaviorSubject<boolean>(false);
  private callActivity: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);
  private callShortlistActivity: BehaviorSubject<any[]> = new BehaviorSubject<
    any[]
  >([]);
  constructor(private http: HttpClient, private authService: AuthService) { }

  getObjectToQueryString(object: any) {
    return Object.keys(object)
      .map((key) => key + "=" + object[key])
      .join("&");
  }

  setTradingLayout(showHeader: boolean = true, showFooter: boolean = true) {
    this.layoutSetting.next({ showHeader, showFooter });
  }

  getTradingLayout(): Observable<{ showHeader; showFooter }> {
    return this.layoutSetting.asObservable();
  }

  getAdvisorCalls(packId: any, query: string): Observable<any> {
    return this.http.get(`${API_END_POINTS.CALLS.advisor_calls}/${packId}?${query}`);
  }

  getTopAdvisors(
    limit: number,
    category: string,
    duration: string
  ): Observable<any> {
    const query = this.getObjectToQueryString({ limit, category, duration });
    return this.http.get(
      `${API_END_POINTS.TRADING_ADVISOR.top_3_advisors}?${query}`
    );
  }

  getPopularAdvisors(
    limit: number,
    category: string,
    duration: string
  ): Observable<any> {
    const query = this.getObjectToQueryString({ limit, category, duration });
    return this.http.get(
      `${API_END_POINTS.TRADING_ADVISOR.popular_advisors}?${query}`
    );
  }

  getHighReturnAdvisors(
    limit: number,
    category: string,
    duration: string
  ): Observable<any> {
    const query = this.getObjectToQueryString({ limit, category, duration });
    return this.http.get(
      `${API_END_POINTS.TRADING_ADVISOR.high_return_advisors}?${query}`
    );
  }

  getGoodPerformerAdvisors(
    limit: number,
    category: string,
    duration: string
  ): Observable<any> {
    const query = this.getObjectToQueryString({ limit, category, duration });
    return this.http.get(
      `${API_END_POINTS.TRADING_ADVISOR.good_performer_advisors}?${query}`
    );
  }

  /**
   * @deperecated this method is deperecated Get All Advisor
   */
  getAllAdvisors(
    category: string,
    pageNumber: number,
    sortBy?: string
  ): Observable<any> {
    let query: string = "";
    if (sortBy) {
      query = this.getObjectToQueryString({ category, pageNumber, sortBy });
    } else {
      query = this.getObjectToQueryString({ category, pageNumber });
    }
    return this.http.get(
      `${API_END_POINTS.TRADING_ADVISOR.all_advisors}?${query}`
    );
  }

  /**
   * @deperecated this method is deperecated
   */
  getAdvisorID(advisorId: string, category: string): Observable<any> {
    const query = this.getObjectToQueryString({ advisorId, category });
    return this.http.get(
      `${API_END_POINTS.TRADING_ADVISOR.advisors_details}?${query}`
    );
  }

  getAdvisorPrevCalls(
    packId: string,
    limit: number,
    category: string
  ): Observable<any> {
    const query = this.getObjectToQueryString({ packId, limit, category });
    return this.http.get(
      `${API_END_POINTS.TRADING_ADVISOR.pack_previous_call}?${query}`
    );
  }

  getPackPrevCalls(
    packId: string,
    limit: number,
    pageNumber: number
  ): Observable<any> {
    const query = this.getObjectToQueryString({ packId, limit, pageNumber });
    return this.http.get(
      `${API_END_POINTS.TRADING_ADVISOR.pack_closed_call}?${query}`
    );
  }
  getSubscriptionPlans(advisorId: string): Observable<any> {
    return this.http.get(
      `${API_END_POINTS.TRADING_ADVISOR.advisor_subscription}/${advisorId}`
    );
  }

  getAdvisorPerformance(
    advisorId: string,
    category: string,
    duration: string,
    investedAmount?: number
  ): Observable<any> {
    let query: string;
    if (investedAmount) {
      query = this.getObjectToQueryString({
        advisorId,
        category,
        duration,
        investedAmount,
      });
    } else {
      query = this.getObjectToQueryString({ advisorId, category, duration });
    }
    return this.http.get(
      `${API_END_POINTS.TRADING_ADVISOR.advisor_performance}?${query}`
    );
  }

  getSubscribedAdvisors(query?: string): Observable<any> {
    if (query) {
      return this.http.get(
        `${API_END_POINTS.TRADING_ADVISOR.subscribed_list}?${query}`
      );
    } else {
      return this.http.get(API_END_POINTS.TRADING_ADVISOR.subscribed_list);
    }
  }

  subscribedAdvData(advisorId: string): Observable<any> {
    return this.http.get(
      `${API_END_POINTS.TRADING_ADVISOR.subscribed_list}?advisorId=${advisorId}`
    );
  }

  getCallById(callID: string, packId: string): Observable<any> {
    return this.http.get(
      `${API_END_POINTS.CALLS.call_details}?callId=${callID}&packId=${packId}`
    );
  }

  shortlistCall(data: {
    advisor: string;
    call: string;
    isShortList: boolean;
    packId?: string;
    type: string
  }): Observable<any> {
    return this.http.post(API_END_POINTS.CALLS.shortlist_call, data);
  }

  shortlistedCalls(): Observable<any> {
    return this.http.get(API_END_POINTS.CALLS.shortlist_call);
  }

  callGraphData(callId: string): Observable<any> {
    return this.http.get(`${API_END_POINTS.CALLS.call_graph}/${callId}`);
  }

  getCallComments(query: string): Observable<any> {
    return this.http.get(`${API_END_POINTS.CHAT.chat}?${query}`);
  }

  topCalls(limit: number, advisorId?: string): Observable<any> {
    let queryData = {};
    if (advisorId) {
      queryData = { limit, advisorId };
    } else {
      queryData = { limit };
    }
    const query = this.getObjectToQueryString(queryData);
    return this.http.get(`${API_END_POINTS.CALLS.top_calls}?${query}`);
  }

  advisorRating(advisorId: string, data: any): Observable<any> {
    return this.http.put(
      `${API_END_POINTS.TRADING_ADVISOR.advisor_rating}/${advisorId}`,
      data
    );
  }
  tradingNews(tradingsymbol: string): Observable<any> {
    return this.http.get(
      `${API_END_POINTS.NEWS.news}?limit=3&page=1&title=${tradingsymbol}`
    );
  }

  /**
   * @deperecated this method is deperecated
   */
  searchAdvisor(advisorName: string): Observable<any> {
    return this.http.get(
      `${API_END_POINTS.TRADING_ADVISOR.search_advisor}?advisorName=${advisorName}`
    );
  }

  getPackClosedCalls(query: any): Observable<any> {
    return this.http.get(
      `${API_END_POINTS.TRADING_ADVISOR.advisor_closed_calls}?${query}`
    );
  }

  tradeCall(callId: string, callData: any, packId: any): Observable<any> {
    return this.http.post(
      `${API_END_POINTS.CALLS.trade_calls}/${callId}?packId=${packId}`,
      callData
    );
  }

  updateBrokerage(data): Observable<any> {
    return this.http.put(`${API_END_POINTS.INVESTOR.brokerage}`, data);
  }

  tradedCallsList(query): Observable<any> {
    return this.http.get(`${API_END_POINTS.CALLS.traded_calls}?${query}`);
  }

  /**
   * @deperecated this method is deperecated Get All Advisor
   */
  getPickrightAdvisor(
    category: string,
    pageNumber: number,
    role: string
  ): Observable<any> {
    let query: string = "";
    query = this.getObjectToQueryString({ category, pageNumber, role });
    return this.http.get(
      `${API_END_POINTS.TRADING_ADVISOR.all_advisors}?${query}`
    );
  }
  dashTradeStat(state: "OPEN" | "CLOSED"): Observable<any> {
    let _advisorId = '';
    if (typeof window !== undefined && window.sessionStorage.getItem('sai')) {
      _advisorId = window.sessionStorage.getItem('sai');
    }
    return this.http.get(
      `${API_END_POINTS.CALLS.dashboard_data}?state=${state}&advisorId=${_advisorId}`
    );
  }

  tradingOrders(tradeCallId: string, packId: any): Observable<any> {
    return this.http.get(
      `${API_END_POINTS.CALLS.trade_orders}?tradeCallId=${tradeCallId}&packId=${packId}`
    );
  }

  tradeOrderDetail(tradeId: string): Observable<any> {
    return this.http.get(`${API_END_POINTS.CALLS.trade_calls}/${tradeId}`);
  }

  getPopupBanner(): Observable<any> {
    return this.http.get(API_END_POINTS.POPUP_BANNER.popup_banner);
  }

  dashboardTopCalls(limit: number): Observable<any> {
    return this.http.get(
      `${API_END_POINTS.CALLS.dash_closed_calls}?limit=${limit}`
    );
  }

  tradeCallsCount(): Observable<any> {
    return this.http.get(`${API_END_POINTS.CALLS.calls_count}`);
  }

  getMarketDeatils(): Observable<any> {
    return this.http.get(API_END_POINTS.CLIENTS.marketDetail);
  }
  playAudioInvestor(type) {
    const _user = this.authService.getUserData();
    if (_user && _user.notificationSound != "ON") {
      return;
    }
    const audio = new Audio();
    switch (type) {
      case "TARGET_ONE":
      case "TARGET_TWO":
        audio.src = "assets/audio/Target_Hit.mp4";
        break;
      case "STOP_LOSS":
        audio.src = "assets/audio/SL_Hit.mp3";
        break;
      case "NEW_CALL":
        audio.src = "assets/audio/New_Call.mp3";
        break;
      case "ACTIVE":
      case "MODIFIED":
      case "COMPLETED":
        audio.src = "assets/audio/Edit.mp3";
        break;
      default:
        break;
    }
    audio.addEventListener(
      "canplaythrough",
      () => {
        this.loadedAudio(audio);
      },
      false
    );
    audio.load();
  }

  private loadedAudio(audio: HTMLAudioElement) {
    audio.play();
  }

  getScripAccuracy(
    tradingSymbol: string,
    advisorId: StringConstructor
  ): Observable<any> {
    return this.http.get(
      `${API_END_POINTS.CALLS.scrip_accuracy}?tradingSymbol=${tradingSymbol}&advisorId=${advisorId}`
    );
  }
  getClosedCall(callId: string, packId: any): Observable<any> {
    return this.http.get(
      `${API_END_POINTS.TRADING_ADVISOR.closed_call_details}?callId=${callId}&packId=${packId}`
    );
  }
  getClosedCallComments(callId: string): Observable<any> {
    return this.http.get(
      `${API_END_POINTS.TRADING_ADVISOR.closed_call_comment}?call=${callId}`
    );
  }
  getAdvisorReview(advisorId: string, page, limit): Observable<any> {
    let query: string = "";
    query = this.getObjectToQueryString({ page, limit });
    return this.http.get(
      `${API_END_POINTS.TRADING_ADVISOR.advisor_review}/${advisorId}?${query}`
    );
  }

  setNewCallAdded() {
    this.hasCallCreated.next(true);
  }

  hasNewcall(): Observable<boolean> {
    return this.hasCallCreated.asObservable();
  }

  saveCallActivity(activity) {
    let activites: any[] = this.callActivity.value;
    activites.push(activity);
    this.callActivity.next(activites);
  }

  getAllCallActivity(): Observable<any[]> {
    return this.callActivity.asObservable();
  }

  seenCallActivity(tab: number) {
    const trackActivites: any[] = [
      "TARGET_ONE",
      "TARGET_TWO",
      "STOP_LOSS",
      "DELETED",
      "MODIFIED",
      "TRADED",
    ];
    if (tab === 2) {
      let activites = this.callActivity.value.filter(
        (x) => x.status !== "EXITED"
      );
      this.callActivity.next(activites);
    } else if (tab === 1) {
      let activites = this.callActivity.value.filter(
        (x) => trackActivites.indexOf(x.status) === -1
      );
      this.callActivity.next(activites);
    }
    {
      let activites = this.callShortlistActivity.value.filter(
        (x) => trackActivites.indexOf(x.status) === -1
      );
      this.callShortlistActivity.next(activites);
    }
  }

  saveShortListCallActivity(activity) {
    let activites: any[] = this.callShortlistActivity.value;
    activites.push(activity);
    this.callShortlistActivity.next(activites);
  }

  getAllShortlistCallActivity(): Observable<any[]> {
    return this.callShortlistActivity.asObservable();
  }

  getAdvisorReviews(advisorId): Observable<any> {
    return this.http.get(
      `${API_END_POINTS.TRADING_ADVISOR.advisor_reviewed}/${advisorId}`
    );
  }
  advisorComplaindDetail(id: any, complaints: any) {
    return this.http.get(
      `${API_END_POINTS.CLIENTS.complaindDetail}/${id}?complaints=${complaints}`
    );
  }
  getAdvisorDocuments(advisorId: string) {
    return this.http.get(`${API_END_POINTS.CLIENTS.documents}/${advisorId}`);
  }
  getClientDetails() {
    return this.http.get(`${API_END_POINTS.TRADING_ADVISOR.client_detail}`);
  }

  getDefaultAdvisorDetail() {
    let _date = new Date();
    _date.setFullYear(_date.getFullYear() - 1);
    return this.http.get(
      `${API_END_POINTS.TRADING_ADVISOR.default_advisor}?duration=${_date}`
    );
  }
  getSubscribedPack() {
    let _advisorId = '';
    if (typeof window !== undefined && window.sessionStorage.getItem('sai')) {
      _advisorId = window.sessionStorage.getItem('sai');
    }
    return this.http.get(`${API_END_POINTS.TRADING_PACK.subscribed_packs}?advisorId=${_advisorId}`);
  }
  getPackTopCalls(packId: any, limit: any) {
    let query: string;
    query = this.getObjectToQueryString({ packId, limit });

    return this.http.get(`${API_END_POINTS.CALLS.top_calls}?${query}`);
  }

  /**
   * @deperecated this method is deperecated
   */
  getSubscriptionData(packId: any) {
    return this.http.get(
      `${API_END_POINTS.TRADING_ADVISOR.get_suscription_pack}${packId}`
    );
  }

  getDefaultTradingPack(date?: any) {
    let _advisorId = '';
    if (typeof window !== undefined && window.sessionStorage.getItem('sai')) {
      _advisorId = window.sessionStorage.getItem('sai');
    }
    if (date) {
      return this.http.get(
        `${API_END_POINTS.TRADING_ADVISOR.default_trading_pack}?duration=${date}&advisorId=${_advisorId}`
      );
    } else {
      return this.http.get(
        `${API_END_POINTS.TRADING_ADVISOR.default_trading_pack}?advisorId=${_advisorId}`
      );
    }
  }

  getSubscription(query: any) {
    return this.http.get(`${API_END_POINTS.TRADING_ADVISOR.my_subscription}?${query}`);
  }

  getPackReview(packId: any) {
    return this.http.get(
      `${API_END_POINTS.TRADING_ADVISOR.pack_reviewed}${packId}`
    );
  }

  getAdvisorByPack(packId: any) {
    return this.http.get(
      `${API_END_POINTS.TRADING_ADVISOR.get_advisor_pack_data}${packId}`
    );
  }

  updatePackReview(packId: any, data: any) {
    return this.http.put(
      `${API_END_POINTS.TRADING_ADVISOR.update_reviewed}${packId}`,
      data
    );
  }

  getAllTradingPack(
    query: any,
    page?: any,
    category?: any,
    sortBy?: any,
    role?: any,
    search?: any,
    limit?: any
  ) {
    // let query;
    // const queryParams = [];
    // if (page !== undefined && page !== null) {
    //   queryParams.push(`pageNumber=${page}`);
    // }
    // if (category !== undefined && category !== null) {
    //   queryParams.push(`category=${category}`);
    // }
    // if (sortBy !== undefined && sortBy !== null && sortBy !== '') {
    //   queryParams.push(`sortBy=${sortBy}`);
    // }
    // if (role !== undefined && role !== null) {
    //   queryParams.push(`role=${role}`);
    // }
    // if (search !== undefined && search !== null && search !== '') {
    //   queryParams.push(`search=${search}`);
    // }
    // queryParams.push(`limit=${limit}`);
    // if(typeof window !== undefined && window.sessionStorage.getItem('sai')){
    //   queryParams.push(`advisorId=${window.sessionStorage.getItem('sai')}`);
    // }
    // if (queryParams.length > 0) {
    //   query = queryParams.join("&");
    // }
    return this.http.get(
      `${API_END_POINTS.TRADING_ADVISOR.getAllTradingPack}?${query}`
    );
  }

  getSubscriptionPlanData(packId: any) {
    return this.http.get(
      `${API_END_POINTS.TRADING_ADVISOR.subscriptionPlanData}${packId}`
    );
  }

  getAdvisorDetails(advisorId: any) {
    return this.http.get(
      `${API_END_POINTS.TRADING_ADVISOR.advisor_detail_data}${advisorId}`
    );
  }

  getAdvisorSubscribePack(advisorId: any, query: any, packToExclude?: any) {
    // let query: string;
    // query = this.getObjectToQueryString({ category, pageNumber, limit });
    if (packToExclude !== null && packToExclude !== undefined) {
      return this.http.get(
        `${API_END_POINTS.TRADING_ADVISOR.advisor_subscribe_pack}${advisorId}/${packToExclude}?${query}`
      );
    } else {
      return this.http.get(
        `${API_END_POINTS.TRADING_ADVISOR.advisor_subscribe_pack}${advisorId}?${query}`
      );
    }
  }

  packDetails(packId: any) {
    return this.http.get(
      `${API_END_POINTS.TRADING_ADVISOR.pack_details}${packId}`
    );
  }

  gewtPackPerformance(
    packId: string,
    category: string,
    duration: string,
    investedAmount?: number
  ) {
    let query: string;
    if (investedAmount) {
      query = this.getObjectToQueryString({
        packId,
        category,
        duration,
        investedAmount,
      });
    } else {
      query = this.getObjectToQueryString({ packId, category, duration });
    }

    return this.http.get(
      `${API_END_POINTS.TRADING_ADVISOR.pack_performance}?${query}`
    );
  }

  packHistoricalReport(packId: any, category: any, duration: any) {
    let query: string;
    query = this.getObjectToQueryString({ packId, category, duration });
    return this.http.get(
      `${API_END_POINTS.TRADING_ADVISOR.historical_report}?${query}`
    );
  }

  packRating(packId: any, page: any, limit: any) {
    let query: string;
    query = this.getObjectToQueryString({ page, limit });

    return this.http.get(
      `${API_END_POINTS.TRADING_ADVISOR.pack_review}${packId}?${query}`
    );
  }

  getOrders(page: any, limit: any) {
    let query: string;
    query = this.getObjectToQueryString({ page, limit });
    return this.http.get(
      `${API_END_POINTS.TRADING_ADVISOR.order_history}?${query}`
    );
  }
  getHoldings(page: any, limit: any) {
    let query: string;
    query = this.getObjectToQueryString({ page, limit })
    return this.http.get(`${API_END_POINTS.TRADING_ADVISOR.holdings}?${query}`)
  }

  marketHours(): Observable<any> {
    return this.http.get(API_END_POINTS.AUTH.market_time)
  }

  recentCalls(queryData: any) {
    let query: string;
    query = this.getObjectToQueryString(queryData)
    return this.http.get(`${API_END_POINTS.CALLS.recent_calls}?${query}`)
  }
  getAdvisorList(query: any) {
    return this.http.get(`${API_END_POINTS.TRADING_ADVISOR.advisor_list}?${query}`)
  }
  realizedEarning(query: any) {
    return this.http.get(`${API_END_POINTS.TRADING_ADVISOR.realized_earning}?${query}`)
  }
  unRealizedEarning(query: any) {
    return this.http.get(`${API_END_POINTS.TRADING_ADVISOR.unrealized_earning}?${query}`)
  }
  currentSubscription(query: any) {
    return this.http.get(`${API_END_POINTS.TRADING_ADVISOR.current_subscription}?${query}`)
  }
  order_sync(data: any) {
    return this.http.post(`${API_END_POINTS.TRADING_ADVISOR.order_sync}`, data);
  }
  getAccuracyConsent(investpackId: any) {
    return this.http.get(`${API_END_POINTS.TRADING_ADVISOR.consent_detail}?consentFor=PACK_STATISTICS&tradingpackId=${investpackId}`)
  }
  acceptAccuracyConsent(data: any) {
    return this.http.post(`${API_END_POINTS.TRADING_ADVISOR.consent_accept}`, data);
  }
  tradeCallCount(){
    return this.http.get(`${API_END_POINTS.ADVISOR.trade_count}`)
  }
  userConsent(){
    return this.http.get(`${API_END_POINTS.TRADING_ADVISOR.user_consent}?vertical=trading_advisory`)
  }
}
